import { ChatHistoryListItemGroups } from "./ChatHistoryListItem";
import { Conversation } from "../../types";
import { Typography } from "@mui/material";
import { useConversationHistory } from "../../hooks/useConversationHistory";

interface ChatHistoryListProps {}

export interface GroupedChatHistory {
  month: string;
  entries: Conversation[];
}

export const FAVORITES_GROUP = "Favorites";
export const RECENT_GROUP = "Recent";

export const groupByMonth = (entries: Conversation[]) => {
  const favoritesGroup: GroupedChatHistory = {
    month: FAVORITES_GROUP,
    entries: [],
  };
  const recentGroup: GroupedChatHistory = { month: RECENT_GROUP, entries: [] };
  const monthGroups: GroupedChatHistory[] = [];
  const currentDate = new Date();

  entries.forEach((entry) => {
    const date = new Date(entry.updated);
    const daysDifference =
      (currentDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    const monthYear = date.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    if (entry.is_favorite) {
      favoritesGroup.entries.push(entry);
      return;
    }

    if (daysDifference <= 7) {
      recentGroup.entries.push(entry);
    } else {
      const existingGroup = monthGroups.find(
        (group) => group.month === monthYear
      );
      if (existingGroup) {
        existingGroup.entries.push(entry);
      } else {
        monthGroups.push({ month: monthYear, entries: [entry] });
      }
    }
  });

  // Sort month groups by most recent update
  monthGroups.sort((a, b) => {
    if (a.entries.length === 0) return 1;
    if (b.entries.length === 0) return -1;
    return (
      new Date(b.entries[0].updated).getTime() -
      new Date(a.entries[0].updated).getTime()
    );
  });

  // Sort entries within each group by most recent update
  [recentGroup, ...monthGroups].forEach((group) => {
    group.entries.sort(
      (a, b) =>
        new Date(b.updated).getTime() - new Date(a.updated).getTime()
    );
  });

  // Sort favorites alphabetically by title
  favoritesGroup.entries.sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  return [favoritesGroup, recentGroup, ...monthGroups];
};

const ChatHistoryList: React.FC<ChatHistoryListProps> = () => {
  const { data: chatHistory } = useConversationHistory();

  let groupedChatHistory;
  if (chatHistory && chatHistory.length > 0) {
    groupedChatHistory = groupByMonth(chatHistory);
  } else {
    return (
      <div className="mt-4 flex justify-center">
        <Typography
          style={{ alignSelf: "center", fontWeight: "400", fontSize: 14 }}
        >
          <span>No chat history.</span>
        </Typography>
      </div>
    );
  }

  return <ChatHistoryListItemGroups groupedChatHistory={groupedChatHistory} />;
};

export default ChatHistoryList;
